import type { CatalogDraftWrite } from "@gobro/contracts";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  buildDraftPayload,
  diffDraft,
  draftFromWorkspace,
  draftSignature,
  type EditableCategory,
  type EditableDraft,
  type EditableGroup,
  type EditableItem,
  type EditableOption,
  itemChangeKind,
  newKey,
  publishedItemMap,
  validateDraft,
} from "./catalog-model";
import type { DashboardSnapshot, Locale, QueuedMutation, Store, WorkspaceCatalog } from "./domain";
import { money } from "./format";
import type { translate } from "./i18n";
import { Icon } from "./icons";

interface CatalogManagerProps {
  locale: Locale;
  pending: QueuedMutation[];
  publishCatalog(expectedVersion: number): Promise<void>;
  saveCatalogDraft(payload: CatalogDraftWrite): Promise<void>;
  snapshot: DashboardSnapshot;
  store: Store | null;
  toggleItem(id: string, available: boolean): Promise<void>;
  toggleStore(): Promise<void>;
  tr(key: Parameters<typeof translate>[1]): string;
}

type DialogState =
  | { categoryKey?: string; kind: "category" }
  | { categoryKey: string; itemKey?: string; kind: "item" }
  | { groupKey?: string; kind: "group" }
  | null;

export function CatalogManager(props: CatalogManagerProps) {
  const { locale, snapshot, store, tr } = props;
  const workspace = snapshot.workspace;
  const serverDraft = workspace.catalogs.find((catalog) => catalog.status === "draft") ?? null;
  const published = workspace.catalogs.find((catalog) => catalog.status === "published") ?? null;

  const baseline = useMemo(() => draftFromWorkspace(workspace), [workspace]);
  const baselineSignature = useMemo(() => draftSignature(baseline), [baseline]);
  const [working, setWorking] = useState<EditableDraft>(baseline);
  const dirty = useMemo(
    () => draftSignature(working) !== baselineSignature,
    [baselineSignature, working],
  );
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  const [notice, setNotice] = useState<{ text: string; tone: "error" | "ok" } | null>(null);
  const [dialog, setDialog] = useState<DialogState>(null);
  const [confirmTarget, setConfirmTarget] = useState<string | null>(null);
  const [showGroups, setShowGroups] = useState(false);
  const [showPublish, setShowPublish] = useState(false);

  useEffect(() => {
    if (!dirtyRef.current) setWorking(baseline);
  }, [baseline]);

  const publishedItems = useMemo(() => publishedItemMap(published), [published]);
  const pendingDraft = props.pending.some(
    (mutation) =>
      mutation.entityKey === `catalog-draft:${store?.id ?? ""}` && mutation.state === "pending",
  );

  const update = (change: (draft: EditableDraft) => EditableDraft) => {
    setNotice(null);
    setWorking((current) => change(current));
  };

  const moveCategory = (key: string, direction: -1 | 1) =>
    update((draft) => ({
      ...draft,
      categories: moveEntry(draft.categories, key, direction),
    }));

  const deleteCategory = (key: string) =>
    update((draft) => ({
      ...draft,
      categories: draft.categories.filter((category) => category.key !== key),
    }));

  const upsertCategory = (nameAr: string, nameFr: string, categoryKey?: string) =>
    update((draft) => {
      if (!categoryKey) {
        return {
          ...draft,
          categories: [...draft.categories, { items: [], key: newKey("category"), nameAr, nameFr }],
        };
      }
      return {
        ...draft,
        categories: draft.categories.map((category) =>
          category.key === categoryKey ? { ...category, nameAr, nameFr } : category,
        ),
      };
    });

  const moveItem = (categoryKey: string, itemKey: string, direction: -1 | 1) =>
    update((draft) => ({
      ...draft,
      categories: draft.categories.map((category) =>
        category.key === categoryKey
          ? { ...category, items: moveEntry(category.items, itemKey, direction) }
          : category,
      ),
    }));

  const deleteItem = (categoryKey: string, itemKey: string) =>
    update((draft) => ({
      ...draft,
      categories: draft.categories.map((category) =>
        category.key === categoryKey
          ? { ...category, items: category.items.filter((item) => item.key !== itemKey) }
          : category,
      ),
    }));

  const upsertItem = (categoryKey: string, item: EditableItem, previousCategoryKey?: string) =>
    update((draft) => {
      let removedIndex = -1;
      const categories = draft.categories.map((category) => {
        if (!previousCategoryKey || category.key !== previousCategoryKey) return category;
        removedIndex = category.items.findIndex((candidate) => candidate.key === item.key);
        return {
          ...category,
          items: category.items.filter((candidate) => candidate.key !== item.key),
        };
      });
      return {
        ...draft,
        categories: categories.map((category) => {
          if (category.key !== categoryKey) return category;
          const items = [...category.items];
          if (removedIndex >= 0 && previousCategoryKey === categoryKey) {
            items.splice(Math.min(removedIndex, items.length), 0, item);
          } else {
            items.push(item);
          }
          return { ...category, items };
        }),
      };
    });

  const deleteGroup = (key: string) =>
    update((draft) => ({
      categories: draft.categories.map((category) => ({
        ...category,
        items: category.items.map((item) => ({
          ...item,
          modifierGroupKeys: item.modifierGroupKeys.filter((groupKey) => groupKey !== key),
        })),
      })),
      modifierGroups: draft.modifierGroups.filter((group) => group.key !== key),
    }));

  const upsertGroup = (group: EditableGroup) =>
    update((draft) => {
      const exists = draft.modifierGroups.some((candidate) => candidate.key === group.key);
      return {
        ...draft,
        modifierGroups: exists
          ? draft.modifierGroups.map((candidate) =>
              candidate.key === group.key ? group : candidate,
            )
          : [...draft.modifierGroups, group],
      };
    });

  const save = async () => {
    const issues = validateDraft(working, locale);
    if (issues.length > 0) {
      setNotice({ text: issues[0] as string, tone: "error" });
      return;
    }
    await props.saveCatalogDraft(buildDraftPayload(working, serverDraft?.editVersion ?? 0));
    setNotice({ text: tr("draftSaved"), tone: "ok" });
  };

  const confirmPublish = async () => {
    if (!serverDraft) return;
    await props.publishCatalog(serverDraft.editVersion);
    setShowPublish(false);
    setNotice({ text: tr("publishQueued"), tone: "ok" });
  };

  const confirmDelete = (token: string, action: () => void) => {
    if (confirmTarget === token) {
      setConfirmTarget(null);
      action();
    } else {
      setConfirmTarget(token);
    }
  };

  const deleteButton = (token: string, label: string, action: () => void) =>
    confirmTarget === token ? (
      <button
        className="danger-button mini"
        key={token}
        type="button"
        onClick={() => confirmDelete(token, action)}
      >
        {tr("confirmDelete")}
      </button>
    ) : (
      <button
        aria-label={label}
        className="icon-button danger"
        key={token}
        type="button"
        onClick={() => confirmDelete(token, action)}
      >
        <Icon name="trash" />
      </button>
    );

  const publishBlocked = !serverDraft || dirty || pendingDraft;
  const publishHint = !serverDraft
    ? tr("saveFirst")
    : dirty
      ? tr("saveFirst")
      : pendingDraft
        ? tr("waitingSync")
        : undefined;

  return (
    <>
      <div className="workspace-heading">
        <div>
          <span className="eyebrow">CATALOG STUDIO</span>
          <h1>{tr("menu")}</h1>
        </div>
        <button
          className={`store-switch ${workspace.profile.acceptingOrders ? "on" : ""}`}
          type="button"
          onClick={() => void props.toggleStore()}
        >
          <span>
            <b>{workspace.profile.acceptingOrders ? tr("storeOpen") : tr("storeClosed")}</b>
            <small>
              {locale === "ar" ? "يظهر التغيير فورًا للزبائن" : "Effet immédiat côté clients"}
            </small>
          </span>
          <i />
        </button>
      </div>

      {notice && (
        <div className={`catalog-notice ${notice.tone}`} role="status">
          {notice.text}
        </div>
      )}

      <div className="catalog-toolbar">
        <div className="catalog-status">
          {published && (
            <span className="catalog-chip">
              {locale === "ar"
                ? `منشور · مراجعة ${published.revision}`
                : `Publié · révision ${published.revision}`}
            </span>
          )}
          {serverDraft && (
            <span className="catalog-chip warn">
              {locale === "ar"
                ? `مسودة · إصدار ${serverDraft.editVersion}`
                : `Brouillon · version ${serverDraft.editVersion}`}
            </span>
          )}
          {dirty && <span className="catalog-chip warn">{tr("unsavedChanges")}</span>}
          {pendingDraft && <span className="catalog-chip off">{tr("waitingSync")}</span>}
        </div>
        <div className="catalog-actions">
          <button
            className="ghost-button"
            type="button"
            onClick={() => setDialog({ kind: "category" })}
          >
            <Icon name="plus" />
            {tr("newCategory")}
          </button>
          <button
            className={`ghost-button ${showGroups ? "active" : ""}`}
            type="button"
            onClick={() => setShowGroups((value) => !value)}
          >
            <Icon name="options" />
            {tr("modifierGroups")}
          </button>
          {dirty && (
            <button
              className="ghost-button"
              type="button"
              onClick={() => {
                setWorking(baseline);
                setNotice(null);
              }}
            >
              {tr("discardChanges")}
            </button>
          )}
          <button
            className="primary-button compact"
            disabled={!dirty || pendingDraft}
            type="button"
            onClick={() => void save()}
          >
            {tr("saveDraft")}
          </button>
          <button
            className="primary-button compact"
            disabled={publishBlocked}
            title={publishHint}
            type="button"
            onClick={() => setShowPublish(true)}
          >
            <Icon name="publish" />
            {tr("publishChanges")}
          </button>
        </div>
      </div>

      {showGroups && (
        <section className="catalog-groups">
          <header>
            <b>{tr("modifierGroups")}</b>
            <button
              className="ghost-button"
              type="button"
              onClick={() => setDialog({ kind: "group" })}
            >
              <Icon name="plus" />
              {tr("newGroup")}
            </button>
          </header>
          {working.modifierGroups.length === 0 ? (
            <p className="catalog-hint">
              {locale === "ar"
                ? "لا توجد مجموعات خيارات بعد. مثال: درجة الحرارة، الإضافات، الحجم."
                : "Aucun groupe d’options. Exemple : piquant, suppléments, taille."}
            </p>
          ) : (
            working.modifierGroups.map((group) => (
              <div className="catalog-group-row" key={group.key}>
                <div>
                  <b>{displayName(group.nameAr, group.nameFr, locale)}</b>
                  <small>
                    {locale === "ar"
                      ? `اختيارات: ${group.minSelections} إلى ${group.maxSelections} · ${group.options.length} خيار`
                      : `Choix : ${group.minSelections} à ${group.maxSelections} · ${group.options.length} options`}
                  </small>
                </div>
                <span className="catalog-group-options">
                  {group.options
                    .slice(0, 4)
                    .map((option) => displayName(option.nameAr, option.nameFr, locale))
                    .join(locale === "ar" ? "، " : ", ")}
                  {group.options.length > 4 ? "…" : ""}
                </span>
                <div className="catalog-row-actions">
                  <button
                    aria-label={tr("editLabel")}
                    className="icon-button"
                    type="button"
                    onClick={() => setDialog({ groupKey: group.key, kind: "group" })}
                  >
                    <Icon name="pencil" />
                  </button>
                  {deleteButton(`group:${group.key}`, tr("deleteLabel"), () =>
                    deleteGroup(group.key),
                  )}
                </div>
              </div>
            ))
          )}
        </section>
      )}

      {working.categories.length === 0 ? (
        <div className="catalog-empty">
          <Icon name="menu" />
          <b>{tr("catalogEmpty")}</b>
          <p>{tr("catalogEmptyHint")}</p>
          <button
            className="primary-button compact"
            type="button"
            onClick={() => setDialog({ kind: "category" })}
          >
            <Icon name="plus" />
            {tr("newCategory")}
          </button>
        </div>
      ) : (
        <div className="catalog-tree">
          {working.categories.map((category, categoryIndex) => (
            <section className="catalog-category" key={category.key}>
              <header>
                <div>
                  <b>{displayName(category.nameAr, category.nameFr, locale)}</b>
                  <span>
                    {locale === "ar"
                      ? `${category.items.length} صنف`
                      : `${category.items.length} articles`}
                  </span>
                </div>
                <div className="catalog-row-actions">
                  <button
                    className="ghost-button compact"
                    type="button"
                    onClick={() => setDialog({ categoryKey: category.key, kind: "item" })}
                  >
                    <Icon name="plus" />
                    {tr("addItem")}
                  </button>
                  <button
                    aria-label={tr("rename")}
                    className="icon-button"
                    type="button"
                    onClick={() => setDialog({ categoryKey: category.key, kind: "category" })}
                  >
                    <Icon name="pencil" />
                  </button>
                  <button
                    aria-label={tr("moveUp")}
                    className="icon-button"
                    disabled={categoryIndex === 0}
                    type="button"
                    onClick={() => moveCategory(category.key, -1)}
                  >
                    <Icon name="up" />
                  </button>
                  <button
                    aria-label={tr("moveDown")}
                    className="icon-button"
                    disabled={categoryIndex === working.categories.length - 1}
                    type="button"
                    onClick={() => moveCategory(category.key, 1)}
                  >
                    <Icon name="down" />
                  </button>
                  {deleteButton(`category:${category.key}`, tr("deleteLabel"), () =>
                    deleteCategory(category.key),
                  )}
                </div>
              </header>
              {category.items.length === 0 ? (
                <p className="catalog-hint padded">
                  {locale === "ar"
                    ? "فئة فارغة. أضف صنفًا واحدًا على الأقل قبل حفظ المسودة."
                    : "Catégorie vide. Ajoutez au moins un article avant d’enregistrer."}
                </p>
              ) : (
                category.items.map((item, itemIndex) => {
                  const publishedItem = publishedItems.get(item.key) ?? null;
                  const changeKind = itemChangeKind(item, publishedItems, published);
                  const isOff = publishedItem ? !publishedItem.available : false;
                  return (
                    <div className={`catalog-item-row ${isOff ? "off" : ""}`} key={item.key}>
                      <span className="item-monogram">
                        {displayName(item.nameAr, item.nameFr, locale).slice(0, 1) || "؟"}
                      </span>
                      <div className="catalog-item-info">
                        <b>{displayName(item.nameAr, item.nameFr, locale) || item.key}</b>
                        <small>
                          {money(item.priceMillimes, locale)}
                          {item.modifierGroupKeys.length > 0 &&
                            (locale === "ar"
                              ? ` · ${item.modifierGroupKeys.length} مجموعة خيارات`
                              : ` · ${item.modifierGroupKeys.length} groupes d’options`)}
                        </small>
                        <span className="catalog-item-chips">
                          {changeKind && (
                            <span className={`catalog-chip ${changeKind}`}>
                              {changeKind === "new" ? tr("newInDraft") : tr("modifiedInDraft")}
                            </span>
                          )}
                          {isOff && <span className="catalog-chip off">{tr("unavailable")}</span>}
                        </span>
                      </div>
                      <div className="catalog-row-actions">
                        <button
                          aria-label={tr("editLabel")}
                          className="icon-button"
                          type="button"
                          onClick={() =>
                            setDialog({
                              categoryKey: category.key,
                              itemKey: item.key,
                              kind: "item",
                            })
                          }
                        >
                          <Icon name="pencil" />
                        </button>
                        <button
                          aria-label={tr("moveUp")}
                          className="icon-button"
                          disabled={itemIndex === 0}
                          type="button"
                          onClick={() => moveItem(category.key, item.key, -1)}
                        >
                          <Icon name="up" />
                        </button>
                        <button
                          aria-label={tr("moveDown")}
                          className="icon-button"
                          disabled={itemIndex === category.items.length - 1}
                          type="button"
                          onClick={() => moveItem(category.key, item.key, 1)}
                        >
                          <Icon name="down" />
                        </button>
                        {deleteButton(`item:${item.key}`, tr("deleteLabel"), () =>
                          deleteItem(category.key, item.key),
                        )}
                      </div>
                      {publishedItem ? (
                        <button
                          aria-label={isOff ? tr("available") : tr("unavailable")}
                          className={`toggle ${isOff ? "" : "on"}`}
                          type="button"
                          onClick={() => void props.toggleItem(publishedItem.id, isOff)}
                        >
                          <i />
                        </button>
                      ) : (
                        <span className="toggle-placeholder" />
                      )}
                    </div>
                  );
                })
              )}
            </section>
          ))}
        </div>
      )}

      {dialog?.kind === "category" && (
        <CategoryDialog
          category={working.categories.find((entry) => entry.key === dialog.categoryKey) ?? null}
          locale={locale}
          onClose={() => setDialog(null)}
          onSubmit={(nameAr, nameFr) => {
            upsertCategory(nameAr, nameFr, dialog.categoryKey);
            setDialog(null);
          }}
          tr={tr}
        />
      )}
      {dialog?.kind === "item" && (
        <ItemDialog
          categories={working.categories}
          categoryKey={dialog.categoryKey}
          groups={working.modifierGroups}
          item={
            working.categories
              .find((entry) => entry.key === dialog.categoryKey)
              ?.items.find((entry) => entry.key === dialog.itemKey) ?? null
          }
          locale={locale}
          onClose={() => setDialog(null)}
          onSubmit={(categoryKey, item, previousCategoryKey) => {
            upsertItem(categoryKey, item, previousCategoryKey);
            setDialog(null);
          }}
          tr={tr}
        />
      )}
      {dialog?.kind === "group" && (
        <GroupDialog
          group={working.modifierGroups.find((entry) => entry.key === dialog.groupKey) ?? null}
          locale={locale}
          onClose={() => setDialog(null)}
          onSubmit={(group) => {
            upsertGroup(group);
            setDialog(null);
          }}
          tr={tr}
        />
      )}
      {showPublish && serverDraft && (
        <PublishDialog
          draft={working}
          locale={locale}
          onClose={() => setShowPublish(false)}
          onConfirm={() => void confirmPublish()}
          published={published}
          tr={tr}
        />
      )}
    </>
  );
}

function CategoryDialog(props: {
  category: EditableCategory | null;
  locale: Locale;
  onClose(): void;
  onSubmit(nameAr: string, nameFr: string): void;
  tr(key: Parameters<typeof translate>[1]): string;
}) {
  const { locale, tr } = props;
  const [nameAr, setNameAr] = useState(props.category?.nameAr ?? "");
  const [nameFr, setNameFr] = useState(props.category?.nameFr ?? "");
  const [error, setError] = useState<string | null>(null);
  return (
    <div className="catalog-modal-backdrop">
      <div aria-modal="true" className="catalog-modal" role="dialog">
        <header>
          <h2>{props.category ? tr("rename") : tr("newCategory")}</h2>
          <button
            aria-label={tr("cancel")}
            className="icon-button"
            type="button"
            onClick={props.onClose}
          >
            ×
          </button>
        </header>
        <form
          className="catalog-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (!nameAr.trim()) {
              setError(
                locale === "ar" ? "اسم الفئة بالعربية إجباري" : "Le nom arabe est obligatoire",
              );
              return;
            }
            props.onSubmit(nameAr.trim(), nameFr.trim());
          }}
        >
          {error && (
            <p className="error-banner" role="alert">
              {error}
            </p>
          )}
          <label>
            {locale === "ar" ? "اسم الفئة بالعربية" : "Nom en arabe"}
            <input value={nameAr} onChange={(event) => setNameAr(event.target.value)} />
          </label>
          <label>
            {locale === "ar" ? "اسم الفئة بالفرنسية (اختياري)" : "Nom en français (optionnel)"}
            <input value={nameFr} onChange={(event) => setNameFr(event.target.value)} />
          </label>
          <div className="dialog-actions">
            <button className="ghost-button" type="button" onClick={props.onClose}>
              {tr("cancel")}
            </button>
            <button className="primary-button compact" type="submit">
              {tr("save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ItemDialog(props: {
  categories: EditableCategory[];
  categoryKey: string;
  groups: EditableGroup[];
  item: EditableItem | null;
  locale: Locale;
  onClose(): void;
  onSubmit(categoryKey: string, item: EditableItem, previousCategoryKey?: string): void;
  tr(key: Parameters<typeof translate>[1]): string;
}) {
  const { locale, tr } = props;
  const [categoryKey, setCategoryKey] = useState(props.categoryKey);
  const [descriptionAr, setDescriptionAr] = useState(props.item?.descriptionAr ?? "");
  const [descriptionFr, setDescriptionFr] = useState(props.item?.descriptionFr ?? "");
  const [imageUrl, setImageUrl] = useState(props.item?.imageUrl ?? "");
  const [modifierGroupKeys, setModifierGroupKeys] = useState<string[]>(
    props.item?.modifierGroupKeys ?? [],
  );
  const [nameAr, setNameAr] = useState(props.item?.nameAr ?? "");
  const [nameFr, setNameFr] = useState(props.item?.nameFr ?? "");
  const [price, setPrice] = useState(props.item ? String(props.item.priceMillimes / 1_000) : "");
  const [sku, setSku] = useState(props.item?.sku ?? "");
  const [error, setError] = useState<string | null>(null);

  const toggleGroup = (key: string) =>
    setModifierGroupKeys((current) =>
      current.includes(key) ? current.filter((entry) => entry !== key) : [...current, key],
    );

  return (
    <div className="catalog-modal-backdrop">
      <div aria-modal="true" className="catalog-modal" role="dialog">
        <header>
          <h2>{props.item ? tr("editLabel") : tr("newItem")}</h2>
          <button
            aria-label={tr("cancel")}
            className="icon-button"
            type="button"
            onClick={props.onClose}
          >
            ×
          </button>
        </header>
        <form
          className="catalog-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (!nameAr.trim()) {
              setError(
                locale === "ar" ? "اسم الصنف بالعربية إجباري" : "Le nom arabe est obligatoire",
              );
              return;
            }
            const priceMillimes = Math.round(Number(price.replace(",", ".")) * 1_000);
            if (!Number.isFinite(priceMillimes) || priceMillimes < 0) {
              setError(
                locale === "ar"
                  ? "أدخل سعرًا صحيحًا بالدينار، مثل 12.500"
                  : "Saisissez un prix valide en dinars, ex. 12.500",
              );
              return;
            }
            if (imageUrl.trim() && !imageUrl.trim().startsWith("https://")) {
              setError(
                locale === "ar"
                  ? "رابط الصورة يجب أن يبدأ بـ https://"
                  : "L’URL de l’image doit commencer par https://",
              );
              return;
            }
            props.onSubmit(
              categoryKey,
              {
                descriptionAr,
                descriptionFr,
                imageUrl,
                key: props.item?.key ?? newKey("item"),
                modifierGroupKeys,
                nameAr,
                nameFr,
                priceMillimes,
                sku,
              },
              props.item ? props.categoryKey : undefined,
            );
          }}
        >
          {error && (
            <p className="error-banner" role="alert">
              {error}
            </p>
          )}
          <div className="form-pair">
            <label>
              {locale === "ar" ? "اسم الصنف بالعربية" : "Nom en arabe"}
              <input value={nameAr} onChange={(event) => setNameAr(event.target.value)} />
            </label>
            <label>
              {locale === "ar" ? "الاسم بالفرنسية (اختياري)" : "Nom en français (optionnel)"}
              <input value={nameFr} onChange={(event) => setNameFr(event.target.value)} />
            </label>
          </div>
          <div className="form-pair">
            <label>
              {locale === "ar" ? "السعر بالدينار (مثل 12.500)" : "Prix en dinars (ex. 12.500)"}
              <input
                dir="ltr"
                inputMode="decimal"
                value={price}
                onChange={(event) => setPrice(event.target.value)}
              />
            </label>
            <label>
              {locale === "ar" ? "الفئة" : "Catégorie"}
              <select value={categoryKey} onChange={(event) => setCategoryKey(event.target.value)}>
                {props.categories.map((category) => (
                  <option key={category.key} value={category.key}>
                    {displayName(category.nameAr, category.nameFr, locale)}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <label>
            {locale === "ar" ? "الوصف بالعربية (اختياري)" : "Description en arabe (optionnel)"}
            <textarea
              value={descriptionAr}
              onChange={(event) => setDescriptionAr(event.target.value)}
            />
          </label>
          <label>
            {locale === "ar" ? "الوصف بالفرنسية (اختياري)" : "Description en français (optionnel)"}
            <textarea
              value={descriptionFr}
              onChange={(event) => setDescriptionFr(event.target.value)}
            />
          </label>
          <div className="form-pair">
            <label>
              {locale === "ar" ? "رابط الصورة https (اختياري)" : "URL de l’image https (optionnel)"}
              <input
                dir="ltr"
                inputMode="url"
                placeholder="https://…"
                value={imageUrl}
                onChange={(event) => setImageUrl(event.target.value)}
              />
            </label>
            <label>
              {locale === "ar" ? "رمز الصنف SKU (اختياري)" : "Référence SKU (optionnel)"}
              <input dir="ltr" value={sku} onChange={(event) => setSku(event.target.value)} />
            </label>
          </div>
          {props.groups.length > 0 && (
            <fieldset className="check-list">
              <legend>{tr("modifierGroups")}</legend>
              {props.groups.map((group) => (
                <label key={group.key}>
                  <input
                    checked={modifierGroupKeys.includes(group.key)}
                    type="checkbox"
                    onChange={() => toggleGroup(group.key)}
                  />
                  <span>
                    <b>{displayName(group.nameAr, group.nameFr, locale)}</b>
                    <small>
                      {group.options
                        .slice(0, 3)
                        .map((option) => displayName(option.nameAr, option.nameFr, locale))
                        .join(locale === "ar" ? "، " : ", ")}
                      {group.options.length > 3 ? "…" : ""}
                    </small>
                  </span>
                </label>
              ))}
            </fieldset>
          )}
          <div className="dialog-actions">
            <button className="ghost-button" type="button" onClick={props.onClose}>
              {tr("cancel")}
            </button>
            <button className="primary-button compact" type="submit">
              {tr("save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface OptionFormRow {
  key: string;
  nameAr: string;
  nameFr: string;
  price: string;
}

function GroupDialog(props: {
  group: EditableGroup | null;
  locale: Locale;
  onClose(): void;
  onSubmit(group: EditableGroup): void;
  tr(key: Parameters<typeof translate>[1]): string;
}) {
  const { locale, tr } = props;
  const [maxSelections, setMaxSelections] = useState(props.group?.maxSelections ?? 1);
  const [minSelections, setMinSelections] = useState(props.group?.minSelections ?? 0);
  const [nameAr, setNameAr] = useState(props.group?.nameAr ?? "");
  const [nameFr, setNameFr] = useState(props.group?.nameFr ?? "");
  const [options, setOptions] = useState<OptionFormRow[]>(
    props.group?.options.map((option) => ({
      key: option.key,
      nameAr: option.nameAr,
      nameFr: option.nameFr,
      price: String(option.priceDeltaMillimes / 1_000),
    })) ?? [
      { key: newKey("option"), nameAr: "", nameFr: "", price: "0" },
      { key: newKey("option"), nameAr: "", nameFr: "", price: "0" },
    ],
  );
  const [error, setError] = useState<string | null>(null);

  const patchOption = (key: string, patch: Partial<OptionFormRow>) =>
    setOptions((current) =>
      current.map((option) => (option.key === key ? { ...option, ...patch } : option)),
    );

  return (
    <div className="catalog-modal-backdrop">
      <div aria-modal="true" className="catalog-modal" role="dialog">
        <header>
          <h2>{props.group ? tr("editLabel") : tr("newGroup")}</h2>
          <button
            aria-label={tr("cancel")}
            className="icon-button"
            type="button"
            onClick={props.onClose}
          >
            ×
          </button>
        </header>
        <form
          className="catalog-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (!nameAr.trim()) {
              setError(
                locale === "ar" ? "اسم المجموعة بالعربية إجباري" : "Le nom arabe est obligatoire",
              );
              return;
            }
            if (options.length === 0 || options.some((option) => !option.nameAr.trim())) {
              setError(
                locale === "ar" ? "كل خيار يحتاج اسمًا عربيًا" : "Chaque option exige un nom arabe",
              );
              return;
            }
            if (minSelections > maxSelections || maxSelections > options.length) {
              setError(
                locale === "ar"
                  ? "راجع حدود الاختيار: الأدنى ≤ الأقصى ≤ عدد الخيارات"
                  : "Vérifiez les limites : min ≤ max ≤ nombre d’options",
              );
              return;
            }
            const parsed: EditableOption[] = [];
            for (const option of options) {
              const priceDeltaMillimes = Math.round(Number(option.price.replace(",", ".")) * 1_000);
              if (!Number.isFinite(priceDeltaMillimes) || priceDeltaMillimes < 0) {
                setError(
                  locale === "ar"
                    ? "فرق السعر يجب أن يكون رقمًا موجبًا"
                    : "Le delta de prix doit être un nombre positif",
                );
                return;
              }
              parsed.push({
                key: option.key,
                nameAr: option.nameAr,
                nameFr: option.nameFr,
                priceDeltaMillimes,
              });
            }
            props.onSubmit({
              key: props.group?.key ?? newKey("group"),
              maxSelections,
              minSelections,
              nameAr,
              nameFr,
              options: parsed,
            });
          }}
        >
          {error && (
            <p className="error-banner" role="alert">
              {error}
            </p>
          )}
          <div className="form-pair">
            <label>
              {locale === "ar" ? "اسم المجموعة بالعربية" : "Nom en arabe"}
              <input value={nameAr} onChange={(event) => setNameAr(event.target.value)} />
            </label>
            <label>
              {locale === "ar" ? "الاسم بالفرنسية (اختياري)" : "Nom en français (optionnel)"}
              <input value={nameFr} onChange={(event) => setNameFr(event.target.value)} />
            </label>
          </div>
          <div className="form-pair">
            <label>
              {locale === "ar" ? "الحد الأدنى للاختيارات" : "Minimum de choix"}
              <input
                dir="ltr"
                inputMode="numeric"
                value={minSelections}
                onChange={(event) => setMinSelections(Math.max(0, Number(event.target.value) || 0))}
              />
            </label>
            <label>
              {locale === "ar" ? "الحد الأقصى للاختيارات" : "Maximum de choix"}
              <input
                dir="ltr"
                inputMode="numeric"
                value={maxSelections}
                onChange={(event) => setMaxSelections(Math.max(1, Number(event.target.value) || 1))}
              />
            </label>
          </div>
          <fieldset className="option-editor">
            <legend>{locale === "ar" ? "الخيارات" : "Options"}</legend>
            {options.map((option) => (
              <div className="option-row" key={option.key}>
                <input
                  aria-label={locale === "ar" ? "اسم الخيار بالعربية" : "Nom en arabe"}
                  placeholder={locale === "ar" ? "الاسم بالعربية" : "Nom en arabe"}
                  value={option.nameAr}
                  onChange={(event) => patchOption(option.key, { nameAr: event.target.value })}
                />
                <input
                  aria-label={locale === "ar" ? "اسم الخيار بالفرنسية" : "Nom en français"}
                  placeholder={locale === "ar" ? "الاسم بالفرنسية" : "Nom en français"}
                  value={option.nameFr}
                  onChange={(event) => patchOption(option.key, { nameFr: event.target.value })}
                />
                <input
                  aria-label={locale === "ar" ? "فرق السعر" : "Delta de prix"}
                  dir="ltr"
                  inputMode="decimal"
                  placeholder="0"
                  value={option.price}
                  onChange={(event) => patchOption(option.key, { price: event.target.value })}
                />
                <button
                  aria-label={tr("deleteLabel")}
                  className="icon-button danger"
                  disabled={options.length <= 1}
                  type="button"
                  onClick={() =>
                    setOptions((current) =>
                      current.filter((candidate) => candidate.key !== option.key),
                    )
                  }
                >
                  <Icon name="trash" />
                </button>
              </div>
            ))}
            <button
              className="ghost-button compact"
              type="button"
              onClick={() =>
                setOptions((current) => [
                  ...current,
                  { key: newKey("option"), nameAr: "", nameFr: "", price: "0" },
                ])
              }
            >
              <Icon name="plus" />
              {locale === "ar" ? "إضافة خيار" : "Ajouter une option"}
            </button>
          </fieldset>
          <div className="dialog-actions">
            <button className="ghost-button" type="button" onClick={props.onClose}>
              {tr("cancel")}
            </button>
            <button className="primary-button compact" type="submit">
              {tr("save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PublishDialog(props: {
  draft: EditableDraft;
  locale: Locale;
  onClose(): void;
  onConfirm(): void;
  published: WorkspaceCatalog | null;
  tr(key: Parameters<typeof translate>[1]): string;
}) {
  const { locale, tr } = props;
  const diff = diffDraft(props.draft, props.published);
  const rows: Array<{ label: string; names: string[] }> = [
    {
      label: locale === "ar" ? "فئات جديدة" : "Catégories ajoutées",
      names: diff.addedCategories,
    },
    {
      label: locale === "ar" ? "فئات محذوفة" : "Catégories supprimées",
      names: diff.removedCategories,
    },
    { label: locale === "ar" ? "أصناف جديدة" : "Articles ajoutés", names: diff.addedItems },
    { label: locale === "ar" ? "أصناف معدّلة" : "Articles modifiés", names: diff.changedItems },
    { label: locale === "ar" ? "أصناف محذوفة" : "Articles supprimés", names: diff.removedItems },
  ];
  const totalChanges =
    diff.addedCategories.length +
    diff.removedCategories.length +
    diff.addedItems.length +
    diff.changedItems.length +
    diff.removedItems.length +
    diff.changedGroups;
  return (
    <div className="catalog-modal-backdrop">
      <div aria-modal="true" className="catalog-modal" role="dialog">
        <header>
          <h2>{tr("publishSummary")}</h2>
          <button
            aria-label={tr("cancel")}
            className="icon-button"
            type="button"
            onClick={props.onClose}
          >
            ×
          </button>
        </header>
        {!props.published && (
          <p className="catalog-hint">
            {locale === "ar"
              ? "سيُنشر هذا الكتالوج لأول مرة ويظهر فورًا للزبائن."
              : "Ce catalogue sera publié pour la première fois et visible immédiatement."}
          </p>
        )}
        {totalChanges === 0 ? (
          <p className="catalog-hint">
            {locale === "ar"
              ? "لا فروق عن النسخة المنشورة حاليًا."
              : "Aucune différence avec la version publiée."}
          </p>
        ) : (
          <div className="publish-diff">
            {rows
              .filter((row) => row.names.length > 0)
              .map((row) => (
                <div key={row.label}>
                  <b>{row.names.length}</b>
                  <span>
                    {row.label}
                    <small>{row.names.slice(0, 6).join(locale === "ar" ? "، " : ", ")}</small>
                  </span>
                </div>
              ))}
            {diff.changedGroups > 0 && (
              <div>
                <b>{diff.changedGroups}</b>
                <span>
                  {locale === "ar" ? "تغييرات على مجموعات الخيارات" : "Groupes d’options modifiés"}
                </span>
              </div>
            )}
          </div>
        )}
        <div className="dialog-actions">
          <button className="ghost-button" type="button" onClick={props.onClose}>
            {tr("cancel")}
          </button>
          <button className="primary-button compact" type="button" onClick={props.onConfirm}>
            <Icon name="publish" />
            {tr("publishConfirm")}
          </button>
        </div>
      </div>
    </div>
  );
}

function displayName(ar: string, fr: string, locale: Locale): string {
  if (locale === "ar") return ar || fr;
  return fr || ar;
}

function moveEntry<T extends { key: string }>(entries: T[], key: string, direction: -1 | 1): T[] {
  const index = entries.findIndex((entry) => entry.key === key);
  const target = index + direction;
  if (index < 0 || target < 0 || target >= entries.length) return entries;
  const next = [...entries];
  const [entry] = next.splice(index, 1);
  next.splice(target, 0, entry as T);
  return next;
}
